'use client';

import { useState, useEffect, useRef } from 'react';
import { ArrowLeftIcon, CheckIcon, CaretDownIcon, XIcon, InfoIcon } from '@phosphor-icons/react';
import { QuestionItem } from '../../shared/question-item';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
} from '../../../components/ui/pagination';
import { PieChart, Pie, Cell } from 'recharts';
import { useSelector, useDispatch } from 'react-redux';
import { toggleSimplifyMode } from '../../../../../../apps/frontend/src/redux/slices/globalSlice';
import { getTestWiseNotebookDetails } from '../../../services/notebooks';
import NotebookDetailLoader from '../../../components/loaders/NotebookLoader';
import { SimplifyModeToggle } from '../../../components/simplifymode/SimplifyMode';
import NotebookQuestionDrawer from '../../../components/drawers/NotebookQuestionDrawer';
import { SlidersIcon } from '@phosphor-icons/react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import { Tooltip } from 'recharts';

export const TestDetail = ({ notebook, onBack }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState('All Qs');
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const user = JSON.parse(localStorage.getItem('userProfile'));
  const [autoStartTimer, setAutoStartTimer] = useState(user?.settings?.toggleQuestionTimer);
  const [stats, setStats] = useState({
    correct: 0,
    wrong: 0,
    unattempted: 0,
  });
  const [initialStats, setInitialStats] = useState({
    totalCorrect: 0,
    totalIncorrect: 0,
    totalUnattempted: 0,
    totalCount: 0,
  });
  const [totalPages, setTotalPages] = useState(1);
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [error, setError] = useState(null);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [isQuestionDrawerOpen, setIsQuestionDrawerOpen] = useState(false);
  const [subjectOptions, setSubjectOptions] = useState([]);

  const dispatch = useDispatch();
  const simplifyMode = useSelector((state) => state.global.simplifyMode);
  const ignoreNextClickRef = useRef(false);

  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (subjects.length > 0) {
      const options = [
        { value: 'all', label: 'Subject' },
        ...subjects.map((subject) => ({
          value: subject._id || subject.id || subject,
          label: subject.title || subject.name || subject,
        })),
      ];
      setSubjectOptions(options);
    }
  }, [subjects]);

  const fetchNotebookData = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * ITEMS_PER_PAGE;

      let filterParams = {};
      const activeFilterLabel = activeFilter.split(' (')[0];

      if (activeFilterLabel === 'Attempted correct') {
        filterParams.correct = true;
      } else if (activeFilterLabel === 'Attempted wrong') {
        filterParams.incorrect = true;
      } else if (activeFilterLabel === 'Not attempted') {
        filterParams.unattempted = true;
      }

      const params = {
        offset,
        limit: ITEMS_PER_PAGE,
        ...filterParams,
        ...(selectedSubject && selectedSubject !== 'all' && { subject: selectedSubject }),
      };

      if (!notebook?.testId) {
        throw new Error('Test ID is missing in notebook data');
      }

      const data = await getTestWiseNotebookDetails(notebook.testId, notebook.questions || [], params);

      const transformedQuestions = data.questions.map((question) => ({
        ...question,
        isCorrect: question.isCorrect,
        isAttempted: question.status !== 'notVisited',
        chapterTitle: question.chapter?.title || 'Unknown Chapter',
        options: question.options || [],
        correctOption: question.correctValue,
        solution: question.solution,
        level: question.level,
        timeTaken: question.timeTaken,
        userAnswer: question.optionOrSoln,
        questionType: question.type,
      }));

      setQuestions(transformedQuestions);
      setSubjects(data.subjects);
      setStats({
        correct: data.stats.correct,
        wrong: data.stats.wrong,
        unattempted: data.stats.unattempted,
      });
      setTotalPages(data.totalPages);

      if (currentPage === 1 && activeFilter === 'All Qs') {
        const total = data.stats.totalCorrect + data.stats.totalIncorrect + data.stats.totalUnattempted;
        setInitialStats({
          totalCorrect: data.stats.totalCorrect,
          totalIncorrect: data.stats.totalIncorrect,
          totalUnattempted: data.stats.totalUnattempted,
          totalCount: total,
        });
      }
    } catch (error) {
      setError('Failed to load notebook details. Please try again.');
      // console.error('Error fetching notebook details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (notebook?.id) {
      fetchNotebookData();
    }
  }, [notebook?.id, currentPage, selectedSubject, activeFilter]);

  const handleFilterChange = (filterLabel) => {
    const label = filterLabel.split(' (')[0];
    setActiveFilter(label);
    setCurrentPage(1);
  };

  const handleSubjectFilter = (subjectId) => {
    setSelectedSubject(subjectId);
    setCurrentPage(1);
  };

  const handleQuestionClick = (index) => {
    if (ignoreNextClickRef.current) {
      ignoreNextClickRef.current = false;
      return;
    }
    setSelectedQuestionIndex(index);
    setIsQuestionDrawerOpen(true);
  };

  const handleQuestionChange = (newIndex) => {
    setSelectedQuestionIndex(newIndex);
  };

  const filters = [
    { id: 'all-qs', label: `All Qs (${initialStats.totalCount})` },
    { id: 'attempted-correct', label: `Attempted correct (${initialStats.totalCorrect})` },
    { id: 'attempted-wrong', label: `Attempted wrong (${initialStats.totalIncorrect})` },
    { id: 'not-attempted', label: `Not attempted (${initialStats.totalUnattempted})` },
  ];

  const total = initialStats.totalCount || 1;
  const pieData = [
    { name: 'Attempted wrong', value: Math.round((initialStats.totalIncorrect / total) * 100) },
    { name: 'Attempted correct', value: Math.round((initialStats.totalCorrect / total) * 100) },
    { name: 'Not Attempted', value: Math.round((initialStats.totalUnattempted / total) * 100) },
  ];

  const COLORS = ['var(--q3-danger-normal)', 'var(--q3-success-normal)', 'var(--q3-neutral-light)'];

  const handleToggleSimplifyMode = () => {
    dispatch(toggleSimplifyMode());
  };

  const renderCustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 rounded px-2 py-1 bg-[var(--q3-surface-default)] shadow text-xs font-medium border border-[var(--q3-stroke-normal)] whitespace-nowrap z-10">
          {`${payload[0].name}: ${payload[0].value.toFixed(2)}%`}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <NotebookDetailLoader />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-6 bg-[var(--q3-surface-default)] rounded-lg">
          <p className="text-[var(--q3-neutral-default)] mb-4">{error}</p>
          <button onClick={fetchNotebookData} className="px-4 py-2 bg-[var(--q3-accent-normal)] text-white rounded-lg">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="min-h-screen bg-[var(--q3-surface-default)] pb-20">
        {/* Mobile Header */}
        <div className="sticky top-0 bg-[var(--q3-surface-default)] border-b border-[var(--q3-stroke-normal)] px-4 py-3 z-10">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={onBack} className="p-2 -ml-2">
              <ArrowLeftIcon size={20} className="text-[var(--q3-neutral-default)]" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-[var(--q3-neutral-default)] truncate">{notebook.title}</h1>
            </div>
          </div>

          {/* Mobile Filters - Updated to match desktop functionality */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
            {/* Subject Dropdown - Matching desktop */}
            <Select value={selectedSubject} onValueChange={handleSubjectFilter}>
              <SelectTrigger className="text-xs rounded-full px-3 py-1.5 pr-3 text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] hover:bg-[var(--q3-surface-dimmest)] transition-colors cursor-pointer h-auto">
                <SlidersIcon size={14} className="mr-1.5" />
                <SelectValue placeholder="Subject" />
              </SelectTrigger>
              <SelectContent>
                {subjectOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="text-xs">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="h-5 border-l border-[var(--q3-stroke-normal)] flex-shrink-0"></div>

            {/* Status Filters - Matching desktop */}
            {filters.map((filter) => {
              const filterLabel = filter.label.split(' (')[0];
              return (
                <button
                  key={filter.id}
                  onClick={() => handleFilterChange(filter.label)}
                  className={`relative flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-colors whitespace-nowrap flex-shrink-0
                  ${
                    activeFilter === filterLabel
                      ? 'bg-[var(--q3-accent-normal)] text-white '
                      : 'text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-dimmest)] border border-[var(--q3-stroke-normal)]'
                  }`}
                >
                  {filter.label}
                  {activeFilter === filterLabel && <CheckIcon size={14} weight="bold" className="ml-1" />}
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-4">
          {/* Mobile Stats Card - Updated to match desktop */}
          <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg p-4 mb-4 mt-4">
            <div className="flex flex-col gap-4">
              <div>
                <h3 className="text-base font-semibold text-[var(--q3-neutral-default)] mb-1">Qs in this notebook</h3>
                <p className="text-xs text-[var(--q3-neutral-light)]">
                  This pie chart shows your attempt status of questions added in this notebook
                </p>
              </div>

              <div className="flex items-center gap-4 -mt-4">
                <div className="w-20 h-20 flex-shrink-0">
                  <PieChart width={80} height={80} className="mt-10">
                    <Pie data={pieData} innerRadius={24} outerRadius={40} dataKey="value" stroke="none">
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                      ))}
                    </Pie>
                    <Tooltip content={renderCustomTooltip} />
                  </PieChart>
                </div>

                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                    <XIcon size={16} weight="bold" className="text-red-400" />
                    <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                      Attempted wrong{' '}
                      <span className="font-semibold text-[var(--q3-neutral-default)]">
                        ({Math.round((stats.wrong / (stats.correct + stats.wrong + stats.unattempted || 1)) * 100)}%)
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                    <CheckIcon size={16} weight="bold" className="text-green-400" />
                    <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                      Attempted correct{' '}
                      <span className="font-semibold text-[var(--q3-neutral-default)]">
                        ({Math.round((stats.correct / (stats.correct + stats.wrong + stats.unattempted || 1)) * 100)}%)
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                    <InfoIcon size={16} weight="bold" className="text-gray-400" />
                    <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                      Not Attempted{' '}
                      <span className="font-semibold text-[var(--q3-neutral-default)]">
                        (
                        {Math.round((stats.unattempted / (stats.correct + stats.wrong + stats.unattempted || 1)) * 100)}
                        %)
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Questions List - Updated to match desktop */}
          <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg mb-4">
            <h3 className="text-base font-semibold text-[var(--q3-neutral-default)] p-3 border-b border-[var(--q3-stroke-normal)]">
              All Bookmarked Qs
            </h3>
            <div className="divide-y divide-[var(--q3-stroke-normal)]">
              {questions.length > 0 ? (
                questions.map((question, index) => (
                  <div
                    key={question.id}
                    className="p-3 hover:bg-[var(--q3-surface-dim)] cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!e.target.closest('.bookmark-button') && !isQuestionDrawerOpen) {
                        handleQuestionClick(index);
                      }
                    }}
                  >
                    <QuestionItem
                      id={question.id}
                      questionid={question.questionId}
                      question={question.question}
                      status={question.status}
                      type={question.type}
                      testInfo={question.chapterTitle}
                      note={question.notes}
                      isBookmarked={true}
                      packId={notebook.packId}
                      testId={question.testId}
                      notebookData={notebook}
                      isAnyDrawerOpen={isQuestionDrawerOpen}
                    />
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-[var(--q3-neutral-light)]">
                    {activeFilter === 'All Qs' && questions.length === 0
                      ? 'No questions found'
                      : `No ${activeFilter.toLowerCase()} questions found`}
                  </p>
                  {activeFilter !== 'All Qs' && (
                    <button
                      onClick={() => setActiveFilter('All Qs')}
                      className="mt-2 px-4 py-2 text-sm bg-[var(--q3-accent-normal)] text-white rounded-lg"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Pagination - Updated to match desktop */}
          {questions.length > 0 && (
            <div className="mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="h-9 w-9 p-0"
                    />
                  </PaginationItem>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        isActive={page === currentPage}
                        onClick={() => setCurrentPage(page)}
                        className="h-9 w-9 p-0"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="h-9 w-9 p-0"
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>

        {/* Simplify Mode Toggle - Fixed at bottom like desktop */}
        {/* <div className="fixed bottom-4 left-0 right-0 flex justify-center px-4">
        <div
          className="inline-flex min-h-[48px] px-4 py-2 items-center gap-3 rounded-[12px] cursor-pointer transition-all duration-300 border bg-[var(--q3-surface-glass)] backdrop-blur-sm"
          style={{
            borderImage: 'linear-gradient(to right, #FF6B00, var(--q3-stroke-light)) 1',
            borderImageSlice: 1,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: '12px',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
          }}
          onClick={() => dispatch(toggleSimplifyMode())}
        >
          <span className="text-sm font-medium text-[var(--q3-neutral-default)]">
            Simplify Mode is {simplifyMode ? 'On' : 'Off'}
          </span>
          <div
            className={`w-12 h-6 rounded-full relative transition-colors duration-300 ${
              simplifyMode ? 'bg-[var(--q3-accent-normal)]' : 'bg-[var(--q3-stroke-normal)]'
            }`}
          >
            <div
              className={`w-5 h-5 bg-white rounded-full absolute top-0.5 shadow-md transition-transform duration-300 ${
                simplifyMode ? 'translate-x-[26px]' : 'translate-x-0.5'
              }`}
            />
          </div>
        </div>
      </div> */}

        {/* Question Drawer - Matching desktop */}
        {isQuestionDrawerOpen && selectedQuestionIndex !== null && (
          <NotebookQuestionDrawer
            isOpen={isQuestionDrawerOpen}
            onClose={() => setIsQuestionDrawerOpen(false)}
            questions={questions}
            currentQuestionIndex={selectedQuestionIndex}
            onQuestionChange={handleQuestionChange}
            testId={notebook.testId}
            packId={notebook.packId}
            autoStartTimer={autoStartTimer}
            onAutoStartTimerChange={setAutoStartTimer}
          />
        )}
      </div>
    );
  }

  // Desktop version
  return (
    <div className="flex-1 p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex flex-col gap-3">
          <button onClick={onBack} className="w-fit p-2 rounded-full bg-[var(--q3-surface-dimmer)]">
            <ArrowLeftIcon size={20} />
          </button>
          <h1 className="text-[var(--q3-neutral-default)] font-bold text-2xl">{notebook.title}</h1>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6 overflow-x-auto whitespace-nowrap">
        <div className="flex items-center gap-4 flex-nowrap min-w-fit">
          {/* Fixed Subject Dropdown */}
          <div className="relative flex items-center flex-shrink-0">
            <Select value={selectedSubject} onValueChange={handleSubjectFilter}>
              <SelectTrigger className="text-sm rounded-full px-4 py-2 pr-4 text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] hover:bg-[var(--q3-surface-dimmest)] transition-colors cursor-pointer">
                <SlidersIcon size={16} className="mr-2" />
                <SelectValue placeholder="Subject" />
              </SelectTrigger>
              <SelectContent>
                {subjectOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="h-6 border-l border-[var(--q3-stroke-normal)] flex-shrink-0"></div>

        {filters.map((filter) => {
          const filterLabel = filter.label.split(' (')[0];
          return (
            <button
              key={filter.id}
              onClick={() => handleFilterChange(filter.label)}
              className={`relative flex items-center gap-1 px-4 py-2 pr-4 rounded-full text-sm transition-colors whitespace-nowrap
                ${
                  activeFilter === filterLabel
                    ? 'bg-[var(--q3-accent-normal)] text-white '
                    : 'text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-dimmest)] border border-[var(--q3-stroke-normal)]'
                }`}
            >
              {filter.label}
              {activeFilter === filterLabel && <CheckIcon size={14} weight="bold" className="ml-1" />}
            </button>
          );
        })}
      </div>

      {/* Stats Card */}
      <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg px-6 py-0 mb-4">
        <div className="flex items-center justify-between gap-6">
          {/* Left Text Section */}
          <div className="min-w-[200px]">
            <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-1">Qs in this notebook</h3>
            <p className="text-xs text-[var(--q3-neutral-light)] m-0 whitespace-normal">
              This pie chart shows your attempt status of questions
              <br />
              added in this notebook
            </p>
          </div>

          {/* Pie Chart */}
          <div className="relative w-20 h-20 flex items-center justify-center ml-auto select-none touch-none">
            {pieData.length > 0 && (
              <PieChart
                width={80}
                height={80}
                className="select-none touch-none"
                style={{ userSelect: 'none', WebkitUserSelect: 'none', outline: 'none' }}
              >
                <Pie
                  data={pieData}
                  innerRadius={24}
                  outerRadius={40}
                  dataKey="value"
                  stroke="none"
                  className="select-none touch-none"
                  style={{ userSelect: 'none', WebkitUserSelect: 'none', outline: 'none' }}
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                      className="select-none touch-none"
                      style={{ userSelect: 'none', WebkitUserSelect: 'none', outline: 'none' }}
                    />
                  ))}
                </Pie>
                <Tooltip content={renderCustomTooltip} />
              </PieChart>
            )}
          </div>

          {/* Status Boxes */}
          <div className="flex flex-row gap-3 text-sm">
            <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
              <XIcon size={16} weight="bold" className="text-red-400" />
              <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                Attempted wrong{' '}
                <span className="font-semibold text-[var(--q3-neutral-default)]">
                  ({Math.round((stats.wrong / (stats.correct + stats.wrong + stats.unattempted || 1)) * 100)}%)
                </span>
              </span>
            </div>

            <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
              <CheckIcon size={16} weight="bold" className="text-green-400" />
              <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                Attempted correct{' '}
                <span className="font-semibold text-[var(--q3-neutral-default)]">
                  ({Math.round((stats.correct / (stats.correct + stats.wrong + stats.unattempted || 1)) * 100)}%)
                </span>
              </span>
            </div>

            <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
              <InfoIcon size={16} weight="bold" className="text-gray-400" />
              <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                Not Attempted{' '}
                <span className="font-semibold text-[var(--q3-neutral-default)]">
                  ({Math.round((stats.unattempted / (stats.correct + stats.wrong + stats.unattempted || 1)) * 100)}%)
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg mb-6 ">
        <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)]  p-4 border-b border-[var(--q3-stroke-normal)]">
          All Bookmarked Qs
        </h3>
        <div className="space-y-0">
          {questions.length > 0 ? (
            questions.map((question, index) => (
              <div
                key={question.id}
                className="pb-4 last:border-b-0 last:pb-0 p-2 border-b border-[var(--q3-stroke-normal)] hover:bg-[var(--q3-surface-dim)] cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  // Only trigger if the click wasn't on the bookmark button and no drawer is open
                  if (!e.target.closest('.bookmark-button') && !isQuestionDrawerOpen) {
                    handleQuestionClick(index);
                  }
                }}
              >
                <QuestionItem
                  id={question.id}
                  questionid={question.questionId}
                  question={question.question}
                  status={question.status}
                  type={question.type}
                  testInfo={question.chapterTitle}
                  note={question.notes}
                  isBookmarked={true}
                  packId={notebook.packId}
                  testId={question.testId}
                  notebookData={notebook}
                  isAnyDrawerOpen={isQuestionDrawerOpen}
                />
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-[var(--q3-neutral-light)]">
                {activeFilter === 'All Qs' && questions.length === 0
                  ? 'No questions found'
                  : `No ${activeFilter.toLowerCase()} questions found`}
              </p>
              {activeFilter !== 'All Qs' && (
                <button
                  onClick={() => setActiveFilter('All Qs')}
                  className="mt-2 px-4 py-2 text-sm bg-[var(--q3-accent-normal)] text-white rounded-lg"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {questions.length > 0 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              />
            </PaginationItem>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <PaginationItem key={page}>
                <PaginationLink isActive={page === currentPage} onClick={() => setCurrentPage(page)}>
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      {isQuestionDrawerOpen && selectedQuestionIndex !== null && (
        <NotebookQuestionDrawer
          isOpen={isQuestionDrawerOpen}
          onClose={() => setIsQuestionDrawerOpen(false)}
          questions={questions}
          currentQuestionIndex={selectedQuestionIndex}
          onQuestionChange={handleQuestionChange}
          testId={notebook.testId}
          packId={notebook.packId}
          autoStartTimer={autoStartTimer}
          onAutoStartTimerChange={setAutoStartTimer}
        />
      )}
    </div>
  );
};
